// S2 (v0.13.0) — tenant scope kept separately from the auth store so the
// super-admin tenant switcher can change scope without touching login state.
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface TenantState {
  currentTenantId: string | null;
  currentTenantName: string;
  currentPlan: string;
  // v0.14.1 — tenant's preferred IANA timezone for display.
  // Default Asia/Kolkata to match the backend column default.
  timezone: string;
  isSuperAdmin: boolean;
  setTenant: (id: string | null, name: string, plan: string) => void;
  setTimezone: (tz: string) => void;
  setIsSuperAdmin: (value: boolean) => void;
  clearTenant: () => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      currentTenantId: null,
      currentTenantName: "",
      currentPlan: "",
      timezone: "Asia/Kolkata",
      isSuperAdmin: false,
      setTenant: (id, name, plan) =>
        set({ currentTenantId: id, currentTenantName: name, currentPlan: plan }),
      setTimezone: (tz) => set({ timezone: tz || "Asia/Kolkata" }),
      setIsSuperAdmin: (value) => set({ isSuperAdmin: value }),
      clearTenant: () =>
        set({
          currentTenantId: null,
          currentTenantName: "",
          currentPlan: "",
          timezone: "Asia/Kolkata",
          isSuperAdmin: false,
        }),
    }),
    {
      name: "sview360.tenant",
    },
  ),
);

// Decode a JWT body (no signature check — that's done server-side).
// Returns a typed view of the claims our frontend cares about.
export interface DecodedToken {
  sub: string;
  tenant_id: string | null;
  is_super_admin: boolean;
  roles: string[];
  exp: number;
}

export function decodeJwt(token: string): DecodedToken | null {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json);
    return {
      sub: claims.sub,
      tenant_id: claims.tenant_id ?? null,
      is_super_admin: Boolean(claims.is_super_admin),
      roles: Array.isArray(claims.roles)
        ? claims.roles
        : claims.role
          ? [claims.role]
          : [],
      exp: claims.exp ?? 0,
    };
  } catch {
    return null;
  }
}
